import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Icon,
  Segment,
  Header,
  Loader,
  Message,
  Confirm,
  Input,
  Dropdown,
} from "semantic-ui-react";
import { SG_GET_STATUS_ORDERS, SG_DELETE_STATUS_ORDER } from "constants/actions";
import PageIntro from '../components/PageIntro';

export default function StatusList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    table: "",
  });

  const {
    status_orders = [],
    pending = false,
    error,
    total = 0,
    pageNumber = 1,
  } = useSelector((state) => state.action_planning_status || {});

  useEffect(() => {
    dispatch({
      type: SG_GET_STATUS_ORDERS,
      payload: {
        pageNumber: 1,
        queryParams: {
          search: filters.search,
          table: filters.table,
        },
      },
    });
  }, [dispatch, filters.search, filters.table]);

  const handleDelete = () => {
    if (selectedId) {
      dispatch({
        type: SG_DELETE_STATUS_ORDER,
        payload: { id: selectedId },
      });
      setDeleteConfirm(false);
      setSelectedId(null);
    }
  };

  const handleEdit = (id) => {
    navigate(`/app/action/status/edit/${id}`);
  };

  const openDeleteConfirm = (id) => {
    setSelectedId(id);
    setDeleteConfirm(true);
  };

  const tableOptions = [
    { key: "", text: "All Tables", value: "" },
    { key: "action", text: "Action", value: "action" },
    { key: "campaign", text: "Campaign", value: "campaign" },
    { key: "event", text: "Event", value: "event" },
    { key: "report", text: "Report", value: "report" },
    { key: "response", text: "Response", value: "response" },
    { key: "email", text: "Email", value: "email" },
  ];

  return (
    <Segment>
      <PageIntro
        title="Status Orders"
        description="Status orders control which status values appear for different tables (actions, events, campaigns, etc.). Use this to configure display and terminal/active behaviors."
        bullets={[
          'Where data comes from: configured by admins and used by UI components across action planning.',
          'What to expect: ordering, terminal flags and per-table mappings.',
          'Next steps: edit or create orders to reflect your organisation workflow.'
        ]}
      />
      <Header as="h2">
        <Icon name="numbered list" />
        <Header.Content>
          Status Orders
          <Header.Subheader>
            Manage status order configurations for action planning
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <Input
          icon="search"
          placeholder="Search status orders..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ marginRight: 10 }}
        />
        <Dropdown
          placeholder="Filter by table"
          selection
          options={tableOptions}
          value={filters.table}
          onChange={(e, { value }) => setFilters({ ...filters, table: value })}
          style={{ marginRight: 10 }}
        />
        <Button
          primary
          icon="plus"
          content="Create New"
          onClick={() => navigate("/app/action/status/create")}
          floated="right"
        />
      </Segment>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {pending ? (
        <Loader active inline="centered">
          Loading status orders...
        </Loader>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>ID</Table.HeaderCell>
                <Table.HeaderCell>Table</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Display Name</Table.HeaderCell>
                <Table.HeaderCell>Order</Table.HeaderCell>
                <Table.HeaderCell>Usage</Table.HeaderCell>
                <Table.HeaderCell>Terminal</Table.HeaderCell>
                <Table.HeaderCell>Error</Table.HeaderCell>
                <Table.HeaderCell>Active</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {status_orders && status_orders.length > 0 ? (
                status_orders.map((statusOrder) => (
                  <Table.Row key={statusOrder.id}>
                    <Table.Cell>{statusOrder.id}</Table.Cell>
                    <Table.Cell>
                      <strong>{statusOrder.table_name}</strong>
                      <br />
                      <small style={{ color: "#666" }}>
                        {statusOrder.table_name_display}
                      </small>
                    </Table.Cell>
                    <Table.Cell>
                      <code>{statusOrder.status_name}</code>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{statusOrder.display_name}</strong>
                    </Table.Cell>
                    <Table.Cell>{statusOrder.order}</Table.Cell>
                    <Table.Cell>{statusOrder.usage_count}</Table.Cell>
                    <Table.Cell>
                      <Icon
                        name={statusOrder.is_terminal ? "flag checkered" : "minus"}
                        color={statusOrder.is_terminal ? "blue" : "grey"}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Icon
                        name={statusOrder.is_error ? "exclamation triangle" : "minus"}
                        color={statusOrder.is_error ? "red" : "grey"}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Icon
                        name={statusOrder.is_active ? "check circle" : "times circle"}
                        color={statusOrder.is_active ? "green" : "red"}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        icon="edit"
                        size="small"
                        onClick={() => handleEdit(statusOrder.id)}
                      />
                      <Button
                        icon="trash"
                        size="small"
                        color="red"
                        onClick={() => openDeleteConfirm(statusOrder.id)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan="10" textAlign="center">
                    No status orders found
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>

            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="10">
                  Total: {total} status orders
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </>
      )}

      <Confirm
        open={deleteConfirm}
        header="Delete Status Order"
        content="Are you sure you want to delete this status order? This action cannot be undone."
        onCancel={() => {
          setDeleteConfirm(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        confirmButton="Delete"
      />
    </Segment>
  );
}
